import React, { useState } from 'react';
import { api } from 'common/frontend/api';
import FileUploader from 'components/FileUploader';

import { useDispatch, useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import { useTranslation } from 'services/localization';
import {
	deleteObject,
	getDownloadURL,
	storageRef,
	uploadBytes,
} from 'common/frontend/firebase/storage';
import { FieldValues } from 'common/frontend/firebase/firestore';
import FormGroup from '@mui/material/FormGroup';
import { FormControlLabel, Switch } from '@mui/material';
import { setOnboardingActionDone } from 'services/onboarding/utils';

interface Props {
	shopId: string;
}

const STORAGE_PATH = 'terms-of-services';

const TermsUpload = (props: Props) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { shopId } = props;
	const [loading, setLoading] = useState<boolean>(false);

	const termsOfService = useSelector(ShopSelectors.activeShopTerms);
	const useLegacyCategoryTermsOverwrite = useSelector(ShopSelectors.useCategoryTermsOverwrite);
	const activeShopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);

	const handleUpload = async (file: File, name: string) => {
		setLoading(true);
		try {
			setLoading(true);
			const ref = storageRef();
			const fileRef = storageRef(ref, `${STORAGE_PATH}/${shopId}/${name}`);
			const fileUpload = await uploadBytes(fileRef, file);
			const fileUrl = await getDownloadURL(fileUpload.ref);

			await api()
				.shops.doc(shopId)
				.public.update({
					termsOfService: {
						name,
						url: fileUrl,
						verified: false,
					},
				});
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		} finally {
			setOnboardingActionDone('TERMS_OF_SERVICE', activeShopAnalytics);
			setLoading(false);
		}
	};

	const handleRemove = async () => {
		setLoading(true);
		const fileName = termsOfService?.name;

		try {
			if (fileName) {
				const ref = storageRef();
				const fileRef = storageRef(ref, `${STORAGE_PATH}/${shopId}/${fileName}`);
				deleteObject(fileRef);
			}
			await api()
				.shops.doc(shopId)
				.public.update({
					termsOfService: FieldValues.delete() as any,
				});
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		} finally {
			setLoading(false);
		}
	};

	const handleLegacyCategoryTermsOverwriteClick = async (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const checked = e.target.checked;
		if (!checked) {
			const result = window.confirm(
				'This is a legacy setting, and once disabled, this cannot be enabled again. By pressing OK you confirm that from now on category-specific terms will no longer overwrite account-level terms.',
			);
			if (!result) return;
			await api().shops.doc(shopId).public.update({
				'legacySettings.useCategoryTermsOverwrite': FieldValues.delete(),
			});
		}
	};

	return (
		<>
			<FileUploader
				file={termsOfService}
				onFileSelected={handleUpload}
				onFileRemoved={handleRemove}
				onError={(err) => {
					dispatch(NotificationActions.showNotification({ message: err, variant: 'error' }));
				}}
				isUploading={loading}
			/>
			{useLegacyCategoryTermsOverwrite && (
				<FormGroup sx={{ mt: 2 }}>
					<FormControlLabel
						control={
							<Switch
								checked={useLegacyCategoryTermsOverwrite}
								onChange={handleLegacyCategoryTermsOverwriteClick}
							/>
						}
						label={t(
							'account.general.legacyCategoryTermsOverwrite',
							'Category-specific terms always overwrite default account terms',
						)}
					/>
				</FormGroup>
			)}
		</>
	);
};

export default TermsUpload;
